import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, Prediction, Quote, CompanyProfile } from '../api';
import { useAuth } from '../AuthContext';

const TIMEFRAMES = ['daily', 'weekly', 'monthly', 'yearly'];

/**
 * Format a market capitalization value.
 * Finnhub returns market cap in millions of USD.
 */
function formatMarketCap(millions?: number | null): string {
  if (millions == null || !isFinite(millions) || millions <= 0) return 'N/A';
  if (millions >= 1_000_000) return `$${(millions / 1_000_000).toFixed(2)}T`;
  if (millions >= 1_000) return `$${(millions / 1_000).toFixed(2)}B`;
  return `$${millions.toFixed(2)}M`;
}

export default function Predictions() {
  const { symbol: urlSymbol, timeframe: urlTimeframe } = useParams<{ symbol?: string; timeframe?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [symbols, setSymbols] = useState<string[]>([]);
  const [symbol, setSymbol] = useState(urlSymbol?.toUpperCase() || '');
  const [timeframe, setTimeframe] = useState(
    urlTimeframe && TIMEFRAMES.includes(urlTimeframe) ? urlTimeframe : 'daily'
  );
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [latestQuote, setLatestQuote] = useState<Quote | null>(null);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const s = user?.stocks || [];
    setSymbols(s);
    if (!symbol && s.length > 0) setSymbol(s[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.stocks]);

  useEffect(() => {
    if (symbol) {
      navigate(`/predictions/${symbol}/${timeframe}`, { replace: true });
    }
  }, [symbol, timeframe, navigate]);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setProfile(null);
    Promise.all([
      api.getPredictions(symbol, timeframe),
      api.getQuotes(symbol, 1),
      api.getProfile(symbol).catch(() => null),
    ])
      .then(([p, q, pr]) => {
        setPredictions(p);
        setLatestQuote(q.length > 0 ? q[q.length - 1] : null);
        setProfile(pr);
      })
      .finally(() => setLoading(false));
  }, [symbol, timeframe]);

  const latest = predictions[0];

  return (
    <div>
      <h2>Analysis</h2>

      <div className="controls">
        <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
          {symbols.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="tabs" style={{ border: 'none', marginBottom: 0 }}>
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              className={timeframe === tf ? 'active' : ''}
              onClick={() => setTimeframe(tf)}
            >
              {tf.charAt(0).toUpperCase() + tf.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading predictions...</div>
      ) : !latest ? (
        <div className="loading">No predictions found for {symbol} ({timeframe})</div>
      ) : (
        <>
          {/* Latest prediction hero card */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3>
              {latest.symbol}
              <span className={`badge ${latest.recommendation === 'Buy' ? 'buy' : 'not-buy'}`}>
                {latest.recommendation}
              </span>
            </h3>
            <div className="meta">{latest.date} &middot; {latest.timeframe}</div>

            <div className="stats-row">
              {latestQuote && (
                <div className="stat">
                  <div className="value">${latestQuote.currentPrice.toFixed(2)}</div>
                  <div className="label">Current Price</div>
                </div>
              )}
              <div className="stat">
                <div className="value">${latest.predictedQuote.toFixed(2)}</div>
                <div className="label">Predicted Price</div>
              </div>
              {latestQuote && (
                <div className="stat">
                  <div className="value" style={{
                    color: latest.predictedQuote >= latestQuote.currentPrice ? 'var(--green)' : 'var(--red)'
                  }}>
                    {latest.predictedQuote >= latestQuote.currentPrice ? '+' : ''}
                    {((latest.predictedQuote - latestQuote.currentPrice) / latestQuote.currentPrice * 100).toFixed(2)}%
                  </div>
                  <div className="label">Expected Change</div>
                </div>
              )}
              <div className="stat">
                <div className="value">{latest.evidenceCount}</div>
                <div className="label">Evidence Points</div>
              </div>
              <div className="stat">
                <div className="value">{formatMarketCap(profile?.marketCapitalization)}</div>
                <div className="label">Market Cap</div>
              </div>
            </div>

            <div className="sentiment-bar">
              <span style={{ color: 'var(--green)' }}>+{latest.positiveSentiments}</span>
              <span style={{ color: 'var(--red)' }}>-{latest.negativeSentiments}</span>
              <span style={{ color: 'var(--yellow)' }}>~{latest.neutralSentiments}</span>
            </div>

            {latest.predictionExplanation && (
              <p style={{ marginTop: 12 }}>{latest.predictionExplanation}</p>
            )}
            {latest.reasoning && (
              <p style={{ marginTop: 8, fontStyle: 'italic' }}>{latest.reasoning}</p>
            )}
          </div>

          {/* History table */}
          {predictions.length > 1 && (
            <div className="card">
              <h3>History</h3>
              <table className="summary-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Recommendation</th>
                    <th>Predicted Price</th>
                    <th>Sentiments (+/-/~)</th>
                    <th>Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {predictions.map((p, i) => (
                    <tr key={i}>
                      <td>{p.date}</td>
                      <td>
                        <span className={`badge ${p.recommendation === 'Buy' ? 'buy' : 'not-buy'}`}>
                          {p.recommendation}
                        </span>
                      </td>
                      <td>${p.predictedQuote.toFixed(2)}</td>
                      <td>{p.positiveSentiments}/{p.negativeSentiments}/{p.neutralSentiments}</td>
                      <td>{p.evidenceCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
